<script setup lang="ts">
import { hydrateOnVisible } from 'vue';
import useLawyerDetail from '@/pages/lawyer/composables/useLawyerDetail';
import useLawyerModals from '@/pages/lawyer/composables/useLawyerModals';
import LawyerPageSkeleton from '@/pages/lawyer/ui/Skeleton/index.vue';
import NavigationLinks from '@/pages/lawyer/ui/NavigationLinks.vue';
import MainSection from '@/pages/lawyer/ui/sections/MainSection/index.vue';
import TimeSection from '@/pages/lawyer/ui/sections/TimeSection/index.vue';
import LawyerCard from '@/widgets/LawyerCard/index';
import { DateTimeModal } from '@/widgets/DateTimeModal';
import { ConsultationModal } from '@/widgets/ConsultationModal';
import { ConfirmationModal } from '@/widgets/ConfirmationModal';
import { CallbackModal } from '@/widgets/CallbackModal';
import { ComplaintModal } from '@/widgets/ComplaintModal';
import { AppSuccessModal } from '@/shared/ui/AppSuccessModal';
import { ImageModal } from '@/widgets/ImageModal';
import AppBreadcrumbs from '@/shared/ui/AppBreadcrumbs/index';
import SchemaOrg from '@/shared/ui/SchemaOrg/index';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';

const ExperienceSection = defineAsyncComponent({
  loader: () => import('@/pages/lawyer/ui/sections/ExperienceSection/index.vue'),
  hydrate: hydrateOnVisible({ rootMargin: '200px' }),
});
const FeedbacksSection = defineAsyncComponent({
  loader: () => import('@/pages/lawyer/ui/sections/FeedbacksSection/index.vue'),
  hydrate: hydrateOnVisible({ rootMargin: '200px' }),
});
const ContactSection = defineAsyncComponent({
  loader: () => import('@/pages/lawyer/ui/sections/ContactSection/index.vue'),
  hydrate: hydrateOnVisible({ rootMargin: '200px' }),
});
const ServicesSection = defineAsyncComponent({
  loader: () => import('@/pages/lawyer/ui/sections/ServicesSection/index.vue'),
  hydrate: hydrateOnVisible({ rootMargin: '200px' }),
});
const CityLawyersSection = defineAsyncComponent({
  loader: () => import('@/pages/lawyer/ui/sections/CityLawyersSection/index.vue'),
  hydrate: hydrateOnVisible({ rootMargin: '200px' }),
});

const { openedModals } = useLawyerModals();

const {
  lawyerInfo,
  breadcrumbs,
  navigationLinks,
  isShowSkeleton,
  isCompany,
  isShowCalendar,
  schemaGraph,
  pageReady,
  pageError,
} = useLawyerDetail();

if (import.meta.server) {
  await pageReady;
  if (pageError.value) {
    throw createError({ statusCode: getErrorStatus(pageError.value), fatal: true });
  }
}
</script>

<template>
  <div class="lawyer-page">
    <LawyerPageSkeleton v-if="isShowSkeleton" />
    <div v-else-if="lawyerInfo" class="lawyer-page__loaded">
      <SchemaOrg :graph="schemaGraph" />

      <div v-if="breadcrumbs.length" class="lawyer-page__breadcrumbs">
        <AppBreadcrumbs :breadcrumbs="breadcrumbs" />
      </div>

      <div class="lawyer-page__main">
        <aside class="lawyer-page__sidebar">
          <LawyerCard :data="lawyerInfo" is-lawyer-page :is-company="isCompany" />
          <div v-if="isShowCalendar" class="lawyer-page__sidebar-time">
            <TimeSection />
          </div>
        </aside>

        <div class="lawyer-page__sections">
          <div class="lawyer-page__navigation-links">
            <NavigationLinks :items="navigationLinks" />
          </div>
          <div class="lawyer-page__navigation-sections">
            <MainSection class="lawyer-section" />
            <ExperienceSection class="lawyer-section" :is-company="isCompany" />
            <FeedbacksSection class="lawyer-section" />
            <ContactSection class="lawyer-section" />
            <ServicesSection class="lawyer-section" />
          </div>
        </div>
      </div>

      <CityLawyersSection class="lawyer-page__city-lawyers" />

      <DateTimeModal v-if="openedModals.dateTime" />
      <ConsultationModal v-if="openedModals.consultation" />
      <ConfirmationModal v-if="openedModals.confirmation" />
      <CallbackModal v-if="openedModals.callback" />
      <ComplaintModal v-if="openedModals.complaint" />
      <AppSuccessModal v-if="openedModals.success" />
      <ImageModal v-if="openedModals.image" />
    </div>
  </div>
</template>

<style lang="less">
.lawyer-page {
  .container-d();

  padding-bottom: 152px;

  &__breadcrumbs {
    margin-top: 30px;
    margin-bottom: 50px;
  }

  &__main {
    display: flex;
    margin-bottom: 59px;
  }

  &__sidebar {
    max-width: 453px;
    width: 31.5%;
    margin-right: 65px;
    flex: 1 1 auto;

    @media @1280 {
      width: 35.3%;
    }

    @media @tablet {
      margin-right: 30px;
    }
  }

  &__sidebar-time {
    position: sticky;
    top: @size-x5;
    margin-top: 28px;
  }

  &__sections {
    position: relative;
    max-width: 681px;
    width: 68.5%;

    @media @1280 {
      width: 64.7%;
    }
  }

  &__navigation-links {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 5;
  }

  &__navigation-sections {
    margin-top: @size-x6;
  }

  &__navigation-sections .lawyer-section {
    margin-bottom: 48px;
  }
}
</style>
